import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ── Store brand metadata — colors & domains verified against official brand assets (May 2026) ──
// Parent: Empire (Sobeys/Safeway/FreshCo/IGA/Thrifty), Loblaw (RCSS/NoFrills/Loblaws/Zehrs/T&T/Atlantic/Maxi/Provigo), Metro (Metro/FoodBasics), Pattison (SaveOn), FCL (Co-op)
const STORE_META: Record<string, { color: string; logo: string; abbr: string }> = {
  // ── Empire Company ────────────────────────────────────────────────────────
  'sobeys':                   { color: '#469E41', logo: 'https://logo.clearbit.com/sobeys.com',                abbr: 'SO'  }, // green — national
  'safeway':                  { color: '#E41720', logo: 'https://logo.clearbit.com/safeway.ca',               abbr: 'SW'  }, // red — Western + ON
  'freshco':                  { color: '#BDD01A', logo: 'https://logo.clearbit.com/freshco.com',              abbr: 'FC'  }, // lime — ON/BC/AB/SK/MB
  'iga':                      { color: '#F30017', logo: 'https://logo.clearbit.com/iga.net',                  abbr: 'IGA' }, // red — QC + NB only
  'thrifty foods':            { color: '#005E44', logo: 'https://logo.clearbit.com/thriftyfoods.com',         abbr: 'TF'  }, // dark green — BC only

  // ── Loblaw Companies ──────────────────────────────────────────────────────
  'no frills':                { color: '#FADC00', logo: 'https://logo.clearbit.com/nofrills.ca',              abbr: 'NF'  }, // yellow — national excl. QC
  'real canadian superstore': { color: '#003DA5', logo: 'https://logo.clearbit.com/realcanadiansuperstore.ca',abbr: 'RC'  }, // blue — Western + ON
  'superstore':               { color: '#003DA5', logo: 'https://logo.clearbit.com/realcanadiansuperstore.ca',abbr: 'SS'  }, // alias
  'loblaws':                  { color: '#000000', logo: 'https://logo.clearbit.com/loblaws.ca',               abbr: 'LB'  }, // black — ON/AB/BC
  'zehrs':                    { color: '#DA291C', logo: 'https://logo.clearbit.com/zehrs.ca',                 abbr: 'ZH'  }, // red — ON only
  't&t supermarket':          { color: '#00843D', logo: 'https://logo.clearbit.com/tntsupermarket.com',       abbr: 'T&T' }, // green — BC/AB/ON/QC
  'atlantic superstore':      { color: '#DA291C', logo: 'https://logo.clearbit.com/atlanticsuperstore.ca',    abbr: 'AS'  }, // red — Atlantic only
  'maxi':                     { color: '#0076A8', logo: 'https://logo.clearbit.com/maxi.ca',                  abbr: 'MX'  }, // blue — QC only
  'provigo':                  { color: '#DA291C', logo: 'https://logo.clearbit.com/provigo.ca',               abbr: 'PR'  }, // red — QC only

  // ── Metro Inc. ────────────────────────────────────────────────────────────
  'metro':                    { color: '#D81E05', logo: 'https://logo.clearbit.com/metro.ca',                 abbr: 'ME'  }, // red — QC + ON
  'food basics':              { color: '#FFB500', logo: 'https://logo.clearbit.com/foodbasics.ca',            abbr: 'FB'  }, // yellow — ON only

  // ── Independents ──────────────────────────────────────────────────────────
  'walmart':                  { color: '#0071CE', logo: 'https://logo.clearbit.com/walmart.ca',               abbr: 'WM'  }, // blue — national
  'giant tiger':              { color: '#C8102E', logo: 'https://logo.clearbit.com/gianttiger.com',           abbr: 'GT'  }, // red — AB/SK/MB/ON/QC/Atlantic
  'save-on-foods':            { color: '#00551B', logo: 'https://logo.clearbit.com/saveonfoods.com',          abbr: 'SF'  }, // dark green — Western only
  'co-op':                    { color: '#CC0000', logo: 'https://logo.clearbit.com/co-op.crs',                abbr: 'CO'  }, // red — Western only
};

const FALLBACK_COLORS = [
  '#ef4444','#3b82f6','#eab308','#f97316','#e11d48',
  '#8b5cf6','#22c55e','#1d4ed8','#16a34a','#dc2626',
];

function getStoreMeta(name: string, i: number) {
  const key = name.toLowerCase();
  for (const [k, v] of Object.entries(STORE_META)) {
    if (key.includes(k)) return v;
  }
  return {
    color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    logo:  '',
    abbr:  name.slice(0, 2).toUpperCase(),
  };
}

/**
 * Distribute N stores evenly around the circle.
 * Alternates between two ring radii so adjacent labels don't collide vertically.
 * Returns angle + radius for each store, preserving original array order.
 */
function distributeEvenly(count: number) {
  const step = 360 / count;
  // Offset so first store isn't straight up (which puts label behind FLYDAR text)
  const startAngle = -60;
  return Array.from({ length: count }, (_, i) => ({
    angle:  ((startAngle + i * step) + 360) % 360,
    radius: i % 2 === 0 ? 0.56 : 0.76,  // alternating inner / outer rings
  }));
}

function estSavings(dealCount: number) {
  const low  = Math.round(dealCount * 0.40);
  const high = Math.round(dealCount * 0.85);
  return `$${low}–$${high}`;
}

// ── Types ────────────────────────────────────────────────────────────────────
export interface RadarStore {
  name:      string;
  dealCount: number;
  color?:    string;
  emoji?:    string;
}

interface Props {
  stores:        RadarStore[];
  postalCode:    string;
  onDone:        () => void;
  onSelectStore: (store: RadarStore) => void;
}

const SWEEP_DURATION = 5000;   // ms per full rotation — slower, more dramatic
const SIZE           = 430;    // canvas size px
const BLIP_R         = 22;     // logo circle radius (px) — 44px diameter

// ── Logo circle component ────────────────────────────────────────────────────
function StoreLogo({
  logo, abbr, color, size = BLIP_R * 2, active,
}: {
  logo: string; abbr: string; color: string; size?: number; active: boolean;
}) {
  const [err, setErr] = useState(false);
  return (
    <div style={{
      width:        size,
      height:       size,
      borderRadius: '50%',
      border:       `2px solid ${color}`,
      boxShadow:    active
        ? `0 0 0 4px ${color}44, 0 0 20px ${color}99`
        : `0 0 10px ${color}66`,
      overflow:     'hidden',
      background:   err || !logo ? color : '#fff',
      display:      'flex',
      alignItems:   'center',
      justifyContent: 'center',
      transition:   'box-shadow 0.2s, transform 0.2s',
      flexShrink:   0,
    }}>
      {!err && logo
        ? <img
            src={logo}
            alt={abbr}
            onError={() => setErr(true)}
            style={{ width: '80%', height: '80%', objectFit: 'contain' }}
          />
        : <span style={{
            color:      '#fff',
            fontSize:   size * 0.28,
            fontWeight: 900,
            lineHeight: 1,
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          }}>
            {abbr}
          </span>
      }
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function FlydarSplash({
  stores, postalCode, onDone, onSelectStore,
}: Props) {
  const [sweepAngle, setSweepAngle] = useState(0);
  const [revealed, setRevealed]     = useState<Set<number>>(new Set());
  const [selected, setSelected]     = useState<number | null>(null);
  const [hovered, setHovered]       = useState<number | null>(null);
  const [exiting, setExiting]       = useState(false);
  const rafRef   = useRef<number>(0);
  const startRef = useRef<number>(0);
  const doneRef  = useRef(false);

  // Assign evenly-spaced positions
  const positions = distributeEvenly(stores.length);
  const radarStores = stores.map((s, i) => {
    const meta = getStoreMeta(s.name, i);
    return {
      ...s,
      angle:  positions[i].angle,
      radius: positions[i].radius,
      color:  s.color ?? meta.color,
      logo:   meta.logo,
      abbr:   meta.abbr,
    };
  });

  // RAF loop — runs indefinitely until user taps a store
  useEffect(() => {
    const tick = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const angle = ((elapsed % SWEEP_DURATION) / SWEEP_DURATION) * 360;
      setSweepAngle(angle);
      setRevealed(prev => {
        const next = new Set(prev);
        radarStores.forEach((s, i) => {
          // Reveal when sweep passes within a 60° trailing window
          const diff = ((angle - s.angle) + 360) % 360;
          if (diff < 60) next.add(i);
        });
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores.length]);

  // Convert polar (angle, fractional-radius) → canvas xy
  const polar = (angle: number, radius: number) => {
    const rad = ((angle - 90) * Math.PI) / 180;
    const r   = radius * (SIZE / 2 - 50);
    return {
      x: SIZE / 2 + Math.cos(rad) * r,
      y: SIZE / 2 + Math.sin(rad) * r,
    };
  };

  const handleBlipTap = useCallback((i: number, store: typeof radarStores[0]) => {
    if (doneRef.current) return;
    setSelected(i);
    setTimeout(() => {
      doneRef.current = true;
      setExiting(true);
      setTimeout(() => onSelectStore(store), 380);
    }, 500);
  }, [onSelectStore, radarStores]);

  const handleCompare = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    setExiting(true);
    setTimeout(onDone, 400);
  }, [onDone]);

  const allFound = revealed.size >= radarStores.length;

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          key="flydar-splash"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.04 }}
          transition={{ duration: 0.45 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: 'radial-gradient(ellipse at 50% 28%, #0d1a12 0%, #060c09 100%)' }}
        >
          {/* Grid overlay */}
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage:
              'linear-gradient(rgba(0,255,128,0.03) 1px, transparent 1px),' +
              'linear-gradient(90deg, rgba(0,255,128,0.03) 1px, transparent 1px)',
            backgroundSize: '36px 36px',
          }} />

          {/* ── FLYDAR logo ── */}
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.45 }}
            className="mb-4 text-center select-none pointer-events-none"
          >
            <div
              className="text-4xl font-black tracking-[0.22em] leading-none"
              style={{
                color: '#00ffaa',
                textShadow: '0 0 22px rgba(0,255,170,0.65), 0 0 48px rgba(0,255,170,0.28)',
              }}
            >
              FLYDAR
            </div>
          </motion.div>

          {/* ── RADAR CANVAS ── */}
          <div className="relative flex-shrink-0" style={{ width: SIZE, height: SIZE }}>

            {/* SVG — rings, crosshairs, sweep wedge */}
            <svg width={SIZE} height={SIZE} className="absolute inset-0 pointer-events-none">
              <defs>
                <radialGradient id="sweepGrad4" cx="50%" cy="50%" r="50%">
                  <stop offset="0%"   stopColor="#39ff14" stopOpacity="0.85" />
                  <stop offset="50%"  stopColor="#39ff14" stopOpacity="0.30" />
                  <stop offset="100%" stopColor="#39ff14" stopOpacity="0"    />
                </radialGradient>
              </defs>

              {/* Range rings */}
              {[0.25, 0.45, 0.65, 0.85, 1.0].map(r => (
                <circle key={r}
                  cx={SIZE/2} cy={SIZE/2}
                  r={(SIZE/2 - 10) * r}
                  fill="none"
                  stroke={r === 1.0 ? 'rgba(0,255,170,0.22)' : 'rgba(0,255,170,0.09)'}
                  strokeWidth={r === 1.0 ? 1.5 : 0.7}
                />
              ))}

              {/* Crosshair lines */}
              {[0, 45, 90, 135, 180, 225, 270, 315].map(a => {
                const rad = ((a - 90) * Math.PI) / 180;
                return (
                  <line key={a}
                    x1={SIZE/2} y1={SIZE/2}
                    x2={SIZE/2 + Math.cos(rad) * (SIZE/2 - 10)}
                    y2={SIZE/2 + Math.sin(rad) * (SIZE/2 - 10)}
                    stroke="rgba(0,255,170,0.06)" strokeWidth={0.7}
                  />
                );
              })}

              {/* Sweep wedge */}
              {(() => {
                const WEDGE = 52;
                const cx = SIZE/2, cy = SIZE/2, r = SIZE/2 - 10;
                const s  = ((sweepAngle - 90) * Math.PI) / 180;
                const e  = ((sweepAngle - 90 - WEDGE) * Math.PI) / 180;
                const x1 = cx + Math.cos(s) * r, y1 = cy + Math.sin(s) * r;
                const x2 = cx + Math.cos(e) * r, y2 = cy + Math.sin(e) * r;
                return (
                  <g>
                    <path
                      d={`M${cx},${cy} L${x2},${y2} A${r},${r} 0 0,1 ${x1},${y1} Z`}
                      fill="url(#sweepGrad4)" opacity={0.88}
                    />
                    <line x1={cx} y1={cy} x2={x1} y2={y1}
                      stroke="#39ff14" strokeWidth={2.5}
                      style={{ filter: 'drop-shadow(0 0 5px #39ff14)' }}
                    />
                  </g>
                );
              })()}

              {/* Center glow */}
              <circle cx={SIZE/2} cy={SIZE/2} r={30}
                fill="none" stroke="rgba(0,255,170,0.15)" strokeWidth={1}/>
              <circle cx={SIZE/2} cy={SIZE/2} r={7} fill="#00ffaa"
                style={{ filter: 'drop-shadow(0 0 8px #00ffaa)' }}/>
              <circle cx={SIZE/2} cy={SIZE/2} r={3} fill="#fff"/>

              {/* Selection pulse rings */}
              {selected !== null && revealed.has(selected) && (() => {
                const store = radarStores[selected];
                const { x, y } = polar(store.angle, store.radius);
                return (
                  <circle cx={x} cy={y} r={BLIP_R + 6}
                    fill="none" stroke={store.color}
                    strokeWidth={2} opacity={0.55}
                    style={{ filter: `drop-shadow(0 0 8px ${store.color})` }}>
                    <animate attributeName="r"
                      values={`${BLIP_R+4};${BLIP_R+24};${BLIP_R+4}`}
                      dur="0.65s" repeatCount="4"/>
                    <animate attributeName="opacity"
                      values="0.7;0;0.7" dur="0.65s" repeatCount="4"/>
                  </circle>
                );
              })()}
            </svg>

            {/* ── HTML overlay — logo blips + labels BELOW each circle ── */}
            {radarStores.map((store, i) => {
              if (!revealed.has(i)) return null;
              const { x, y } = polar(store.angle, store.radius);
              const isSel    = selected === i;
              const isHov    = hovered === i;
              const active   = isSel || isHov;

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.55 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.35, ease: 'backOut' }}
                  className="absolute flex flex-col items-center"
                  style={{
                    left:      x,
                    top:       y,
                    // Centre the circle on the blip point
                    transform: 'translate(-50%, -50%)',
                    zIndex:    active ? 40 : 20,
                    // Extra bottom padding to accommodate the label
                    paddingBottom: 0,
                  }}
                >
                  {/* ── Logo circle (tappable button) ── */}
                  <button
                    style={{
                      width:      BLIP_R * 2,
                      height:     BLIP_R * 2,
                      padding:    0,
                      background: 'transparent',
                      border:     'none',
                      cursor:     'pointer',
                      outline:    'none',
                      transform:  active ? 'scale(1.2)' : 'scale(1)',
                      transition: 'transform 0.18s ease',
                      flexShrink: 0,
                    }}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => handleBlipTap(i, store)}
                    title={`Select ${store.name}`}
                  >
                    <StoreLogo
                      logo={store.logo}
                      abbr={store.abbr}
                      color={store.color}
                      size={BLIP_R * 2}
                      active={active}
                    />
                  </button>

                  {/* ── Label BELOW the circle ── */}
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.18, duration: 0.22 }}
                    className="pointer-events-none select-none flex flex-col items-center"
                    style={{
                      marginTop:  5,
                      width:      96,
                      textAlign:  'center',
                    }}
                  >
                    {/* Store name */}
                    <div
                      className="text-[9px] font-black leading-tight whitespace-nowrap overflow-hidden text-ellipsis w-full"
                      style={{
                        color:      active ? store.color : 'rgba(255,255,255,0.88)',
                        textShadow: active ? `0 0 10px ${store.color}` : 'none',
                        transition: 'color 0.2s, text-shadow 0.2s',
                      }}
                    >
                      {store.name}
                    </div>

                    {/* Savings */}
                    {store.dealCount > 0 && (
                      <div className="text-[8px] font-semibold whitespace-nowrap mt-0.5"
                        style={{ color: '#39ff14', opacity: 0.8 }}>
                        Est. {estSavings(store.dealCount)}
                      </div>
                    )}

                    {/* Deal count badge */}
                    {store.dealCount > 0 && (
                      <div
                        className="inline-flex items-center mt-0.5 px-1.5 py-px rounded-full text-[7px] font-bold"
                        style={{
                          background: `${store.color}1e`,
                          border:     `1px solid ${store.color}44`,
                          color:      store.color,
                        }}
                      >
                        {store.dealCount} deals
                      </div>
                    )}

                    {/* Hover / locked hints */}
                    {isHov && !isSel && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        className="text-[7px] font-mono mt-0.5 whitespace-nowrap"
                        style={{ color: store.color, opacity: 0.8 }}>
                        TAP TO SELECT ↗
                      </motion.div>
                    )}
                    {isSel && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        className="text-[7px] font-mono mt-0.5 whitespace-nowrap"
                        style={{ color: '#39ff14' }}>
                        ✓ LOCKED IN…
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>
              );
            })}

            {/* Postal code watermark */}
            <div
              className="absolute pointer-events-none text-center"
              style={{
                left:       '50%',
                top:        '50%',
                transform:  'translate(-50%, 16px)',
                width:      120,
                marginLeft: -60,
              }}
            >
              <div className="text-[10px] font-mono tracking-wider"
                style={{ color: 'rgba(0,255,170,0.50)' }}>
                {postalCode.toUpperCase()}
              </div>
            </div>
          </div>

          {/* ── STATUS BAR ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
            className="mt-4 text-center w-full px-8"
          >
            <div className="text-[11px] font-mono tracking-widest mb-2"
              style={{ color: 'rgba(0,255,170,0.55)' }}>
              {selected !== null
                ? `LOCKING IN ${radarStores[selected]?.name.toUpperCase()}…`
                : allFound
                  ? `${radarStores.length} STORES FOUND — TAP A STORE TO BEGIN`
                  : 'SCANNING NEARBY FLYERS…'}
            </div>

            {/* Pulse bar while scanning, solid when done */}
            {!allFound ? (
              <div className="w-48 h-[2px] mx-auto rounded-full overflow-hidden"
                style={{ background: 'rgba(0,255,170,0.10)' }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: 'linear-gradient(90deg,#00cc88,#39ff14)',
                    boxShadow:  '0 0 8px #39ff14',
                    width:      '40%',
                  }}
                  animate={{ x: ['-120%', '320%'] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                />
              </div>
            ) : (
              <div className="w-48 h-[2px] mx-auto rounded-full"
                style={{
                  background: 'linear-gradient(90deg,#00cc88,#39ff14)',
                  boxShadow:  '0 0 8px #39ff14',
                }}
              />
            )}

            <div className="mt-1.5 text-[10px] font-mono"
              style={{ color: 'rgba(0,255,170,0.32)' }}>
              {revealed.size} / {radarStores.length} stores detected
            </div>
          </motion.div>

          {/* ── Tap hint (after all found) ── */}
          <AnimatePresence>
            {allFound && selected === null && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="mt-2 text-center pointer-events-none"
              >
                <span className="text-[11px] font-semibold tracking-wide"
                  style={{ color: 'rgba(0,255,170,0.42)' }}>
                  👆 Tap a store to build your meal plan
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Compare Stores button ── */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.4 }}
            className="mt-4"
          >
            <button
              onClick={handleCompare}
              className="flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold tracking-wide transition-all hover:scale-105 active:scale-95"
              style={{
                background: 'rgba(0,255,170,0.07)',
                border:     '1px solid rgba(0,255,170,0.22)',
                color:      '#00ffaa',
              }}
            >
              <span>⚖️</span> Compare Stores
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
